import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function CertificationsPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Certifications & Accreditations",
    "description": "Industry-leading accreditations including ISO 9001, NSI Gold, BAFE, SSAIB, and compliance with British Standards BS 5839, BS 8418, BS EN 50131, and BS 7671.",
    "url": `${siteUrl}/certifications`,
    "mainEntity": {
      "@type": "Organization",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "certification",
          "name": "ISO 9001 Certified"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "certification",
          "name": "NSI Gold Approved"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "certification",
          "name": "BAFE Registered"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "certification",
          "name": "SSAIB Accredited"
        }
      ]
    }
  };

  const certifications = [
    {
      title: 'GDPR Compliant',
      description: 'Full compliance with data protection regulations ensuring client information security',
      icon: 'ri-lock-line'
    },
    {
      title: 'First Aid at Work Instructor',
      description: 'TQUK Endorsed instructor qualification for workplace first aid training delivery',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Paediatric First Aid Instructor',
      description: 'Accredited instructor for paediatric first aid training and certification',
      icon: 'ri-parent-line'
    },
    {
      title: 'AED Instructor',
      description: 'Certified to deliver automated external defibrillator training',
      icon: 'ri-heart-add-line'
    },
    {
      title: 'Security Management Diploma',
      description: 'Professional qualification in security operations and management',
      icon: 'ri-shield-star-line'
    },
    {
      title: 'Close Protection Specialist',
      description: 'Specialist modules in close protection and executive security operations',
      icon: 'ri-user-shield-line'
    }
  ];

  const partners = [
    'Hikvision', 'Dahua Technology', 'Honeywell', 'Bosch Security', 
    'Paxton Access', 'Texecom', 'Pyronix', 'Galaxy'
  ];

  const standards = [
    {
      code: 'BS 5839',
      title: 'Fire Detection & Alarm Systems',
      description: 'Full compliance with British Standards for fire detection and alarm systems for buildings'
    },
    {
      code: 'BS 8418',
      title: 'CCTV Installation & Operation',
      description: 'Adherence to standards for installation and remote monitoring of CCTV systems'
    },
    {
      code: 'BS EN 50131',
      title: 'Intruder Alarm Systems',
      description: 'Compliance with European standards for intruder and hold-up alarm systems'
    },
    {
      code: 'BS 7671',
      title: 'Electrical Installation Requirements',
      description: 'IET Wiring Regulations compliance for all electrical installation work'
    }
  ];

  return (
    <>
      <SEO
        title="Certifications & Accreditations - JL Security North Wales"
        description="JL Security maintains industry-leading qualifications and works with accredited partners to ensure all installations and services meet current UK regulations and British Standards including BS 5839, BS 8418, BS EN 50131 and BS 7671."
        keywords="security certifications, accredited training, British Standards compliance, qualified security consultant, North Wales"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar isScrolled={isScrolled} />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h6 className="text-red-600 font-semibold text-sm uppercase tracking-wider mb-4 font-inter">Certifications & Compliance</h6>
              <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight font-poppins">
                Qualified Professionals, Accredited Partners
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed font-inter">
                JL Security maintains industry-leading qualifications and works with accredited specialist partners to ensure all installations and services meet current UK regulations and British Standards.
              </p>
            </div>
          </div>
        </section>

        {/* Certifications Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 font-poppins">
                Professional Qualifications
              </h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto font-inter">
                Qualified in enforcement, conflict management, fire safety and frontline operational compliance, with real-world experience across defence, NHS, nuclear, public sector and private operations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-5">
                    <i className={`${cert.icon} text-2xl text-red-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">{cert.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed font-inter">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* British Standards Compliance */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 font-poppins">
                Compliance with UK Regulations
              </h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto font-inter">
                All installations and services coordinated through JL Security are delivered by accredited partners in full compliance with current UK regulations and British Standards.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {standards.map((standard, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm font-poppins">{standard.code.split(' ')[0]}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 font-poppins">{standard.code}</h3>
                      <h4 className="text-base font-semibold text-red-600 mb-3 font-inter">{standard.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed font-inter">{standard.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100">
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-4 font-poppins">
                Trusted Technology Partners
              </h3>
              <p className="text-base text-gray-600 text-center mb-12 max-w-2xl mx-auto font-inter">
                We partner with the world's leading security technology manufacturers to deliver cutting-edge solutions
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {partners.map((partner, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                  >
                    <span className="text-base font-semibold text-gray-700 font-inter">{partner}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Guarantee */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-50 rounded-2xl p-12 border border-red-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-3xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Compliance Guarantee</h4>
                  <p className="text-base text-gray-700 leading-relaxed mb-4 font-inter">
                    All our installations and services are fully compliant with current UK regulations including BS 5839 for fire detection systems, BS 8418 for CCTV, and BS EN 50131 for intruder alarm systems.
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed font-inter">
                    We ensure your business meets all legal requirements and industry standards, providing complete peace of mind and protection against regulatory penalties. Our team stays current with all regulatory changes to keep your systems compliant.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6 font-poppins">
              Need Certified Security Solutions?
            </h2>
            <p className="text-lg text-red-100 mb-8 font-inter">
              Work with a fully accredited team that delivers compliant, high-quality security systems
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-inter"
              >
                Get in Touch
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href="tel:07834561015"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white hover:text-red-600 transition-all duration-300 whitespace-nowrap cursor-pointer font-inter"
              >
                <i className="ri-phone-line mr-2"></i>
                07834 561015
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
